/**
 * Module-level "a voice call is in progress" flag, kept in sync by the
 * KaiVoice store. Lives outside that store so sound-playing code in
 * Ticket.ts can read it without an import cycle (KaiVoice already imports
 * Ticket). Busy covers requestingMic → ending only — the post-call
 * ended/error screens must not stay muted.
 */
let voiceCallBusy = false;
let voiceCallEndedAt = 0;

// The final spoken turn persists AFTER teardown (the graph settles first), so
// its mirrored comments arrive when the call is no longer "busy" — without a
// grace window they ping right after hanging up.
const SOUND_GRACE_AFTER_CALL_MS = 12_000;

export const setVoiceCallBusy = (busy: boolean): void => {
  if (voiceCallBusy && !busy) {
    voiceCallEndedAt = Date.now();
  }
  voiceCallBusy = busy;
};

export const isVoiceCallBusy = (): boolean => voiceCallBusy;

/** Sounds stay muted while busy AND for a short trailing window after the call. */
export const shouldMuteWidgetSounds = (): boolean =>
  voiceCallBusy || Date.now() - voiceCallEndedAt < SOUND_GRACE_AFTER_CALL_MS;

// ---------------------------------------------------------------------------
// Deferred navigation: server pushes that navigate the widget (open the bug
// form after a voice action, switch conversation) must not run underneath the
// call overlay — it covers everything, including the post-call ended screen.
// Handlers queue here while the overlay is up; the KaiVoice store drains the
// queue when the overlay unmounts (callState back to idle).

let voiceOverlayVisible = false;
let deferredCallActions: Array<() => void> = [];

export const setVoiceOverlayVisible = (visible: boolean): void => {
  voiceOverlayVisible = visible;
};

// Registered by the KaiVoice store: hands an action over mid-call (waits for
// Kai's spoken handover line, then hangs up straight into it).
let voiceHandoffHandler: ((action: () => void) => void) | null = null;

export const setVoiceHandoffHandler = (handler: ((action: () => void) => void) | null): void => {
  voiceHandoffHandler = handler;
};

export const runOrDeferUntilCallOver = (action: () => void): void => {
  if (!voiceOverlayVisible) {
    action();
    return;
  }
  if (voiceHandoffHandler) {
    voiceHandoffHandler(action);
    return;
  }
  deferredCallActions.push(action);
};

export const drainDeferredCallActions = (): void => {
  const actions = deferredCallActions;
  deferredCallActions = [];
  for (const action of actions) {
    try {
      action();
    } catch (_) {}
  }
};
